/**
 * i18n initialization for the Rudi web app.
 *
 * Language starts from the stored user's saved preference, else the browser
 * preference, and is later synced from the profile when one exists.
 *
 * The startup code carries only the English text the first screens use
 * (startupKeys.json); the rest loads namespace by namespace (catalogs.ts).
 * Another language's startup keys load when it is first used: a language
 * switch waits for them and for every namespace loaded so far, so the screen
 * never shows missing keys. The startup language is loaded before the
 * application renders (`initialLanguageReady`).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en, { complete as englishComplete } from 'virtual:rudi-startup-catalog/en';

import {
  CATALOGS_ADDED_EVENT,
  connectCatalogs,
  loadNamespaceOfMissingKey,
  markCatalogComplete,
  prepareLanguage,
} from './catalogs';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getInitialLanguage,
  normalizeLanguage,
} from './languages';

export {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getBrowserDefaultLanguage,
  getInitialLanguage,
  isSupportedLanguage,
  normalizeLanguage,
  type SupportedLanguage,
} from './languages';
export { loadCatalogs, withCatalogs, type CatalogNamespace } from './catalogs';

function setDocumentLanguage(language: unknown): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = normalizeLanguage(language) ?? DEFAULT_LANGUAGE;
}

i18n.on('languageChanged', setDocumentLanguage);

// English initializes synchronously from the bundle.
i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  returnNull: false,
  // A key whose namespace has not arrived loads it; the views then render
  // again on CATALOGS_ADDED_EVENT.
  saveMissing: true,
  missingKeyHandler: (_languages, _namespace, key) => loadNamespaceOfMissingKey(key),
  react: { bindI18n: `languageChanged ${CATALOGS_ADDED_EVENT}` },
});
if (englishComplete) markCatalogComplete(DEFAULT_LANGUAGE);
connectCatalogs(i18n);

// Every switch, from any caller, waits for the language's catalogs first. A
// language that fails to load keeps the current one.
const switchLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (language, callback) => {
  const supported = normalizeLanguage(language);
  if (supported === null) return switchLanguage(language, callback);
  return prepareLanguage(supported).then(
    () => switchLanguage(language, callback),
    (error: unknown) => {
      console.warn(`[i18n] Loading ${supported} failed:`, error);
      return i18n.t;
    }
  );
};

setDocumentLanguage(i18n.language);

const initialLanguage = getInitialLanguage();

/**
 * Resolves once the first screen's language is loaded and active, which the
 * entry points wait for before they render. Never rejects: a failed load
 * keeps English.
 */
export const initialLanguageReady: Promise<void> =
  initialLanguage === DEFAULT_LANGUAGE
    ? Promise.resolve()
    : i18n.changeLanguage(initialLanguage).then(
        () => undefined,
        (error: unknown) => console.warn('[i18n] Loading the startup language failed:', error)
      );

export default i18n;
